import React from 'react';
import SearchField from './SearchField';
import { CustomerOption } from './ProductRangeTypes';

interface ProductRangeCustomerSelectorProps {
  availableCustomers: CustomerOption[]
  selectedCustomerObjects: CustomerOption[]
  updateFilter: (filterKey: 'searchTerm', value: string) => void
  toggleCustomerSelection: (customerId: string) => void
  addAllCustomers: () => void
  clearSelectedCustomers: () => void
}

export const ProductRangeCustomerSelector: React.FC<ProductRangeCustomerSelectorProps> = ({
  availableCustomers,
  selectedCustomerObjects,
  updateFilter,
  toggleCustomerSelection,
  addAllCustomers,
  clearSelectedCustomers,
}) => (
  <div className="bg-white rounded-lg shadow p-6 flex flex-col gap-4">
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Customers</h2>
        <p className="text-sm text-gray-500">Search for customers, add them to the selection, and manage their product family setup.</p>
      </div>
    </div>

    <div className="flex flex-col gap-2">
      <SearchField onSearch={(query) => updateFilter('searchTerm', query)} placeholder="Search customer, site, company or sales company" autofocus={false} />
    </div>

    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 bg-slate-50 px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="text-sm font-semibold text-gray-900">Available customers ({availableCustomers.length})</div>
            <button type="button" onClick={addAllCustomers} disabled={availableCustomers.length === 0} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50">
              Add all
            </button>
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto divide-y divide-gray-200">
          {availableCustomers.map((customer) => (
            <button key={customer.id} type="button" onClick={() => toggleCustomerSelection(customer.id)} className="w-full text-left px-4 py-3 bg-white hover:bg-gray-50">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{customer.customerName}</div>
                  <div className="text-xs text-gray-500">{customer.id} - {customer.siteId} - {customer.company}</div>
                </div>
                <div className="rounded-md bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700">Add</div>
              </div>
            </button>
          ))}
          {availableCustomers.length === 0 && (
            <div className="px-4 py-10 text-sm text-gray-500 text-center">No available customers match your search.</div>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 bg-sky-50 px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="text-sm font-semibold text-gray-900">Selected customers</div>
            <button type="button" onClick={clearSelectedCustomers} disabled={selectedCustomerObjects.length === 0} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50">
              Clear all
            </button>
          </div>
        </div>
        <div className="max-h-[260px] overflow-y-auto divide-y divide-gray-200">
          {selectedCustomerObjects.map((customer) => (
            <div key={customer.id} className="px-4 py-3 bg-sky-100">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{customer.customerName}</div>
                  <div className="text-xs text-gray-500">{customer.id} - {customer.siteId} - {customer.company}</div>
                </div>
                <button type="button" onClick={() => toggleCustomerSelection(customer.id)} className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200">
                  Remove
                </button>
              </div>
            </div>
          ))}
          {selectedCustomerObjects.length === 0 && (
            <div className="px-4 py-10 text-sm text-gray-500 text-center">No customers selected.</div>
          )}
        </div>
      </div>
    </div>
  </div>
);
